import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { t } from '../i18n/nl.js';

// Publieke route, buiten <ProtectedRoute> (zelfde patroon als
// /account-verwijderen en /overeenkomst/:token in App.jsx). Vangt de
// Supabase recovery-link op die vanuit de app-flow (useAuth.resetPassword)
// wordt verstuurd, nu met redirectTo naar deze pagina i.p.v. het mobiele
// deep-link-scheme.
//
// Supabase stuurt het recovery-token als hash-fragment
// (#access_token=...&type=recovery), niet als query-parameter. De
// Supabase-client (detectSessionInUrl: true, zie ../lib/supabase.js) leest
// dit fragment zelf uit en vuurt een 'PASSWORD_RECOVERY'-auth-event. Een
// verlopen/al-gebruikte link geeft i.p.v. een token een foutfragment
// (#error=access_denied&error_code=otp_expired&...).
export default function ResetWachtwoordPage() {
  const [status, setStatus] = useState('laden'); // laden | klaar | ongeldig | succes
  const [nieuwWachtwoord, setNieuwWachtwoord] = useState('');
  const [herhaalWachtwoord, setHerhaalWachtwoord] = useState('');
  const [error, setError] = useState('');
  const [opslaan, setOpslaan] = useState(false);
  const navigate = useNavigate();
  // Blijft `false` totdat het wachtwoord succesvol is opgeslagen; gebruikt
  // door de unmount-cleanup hieronder om te bepalen of een nooit-afgeronde
  // recovery-sessie moet worden opgeruimd (signOut) als de gebruiker
  // wegnavigeert vóór afronding. Dekt niet het sluiten van tabblad/browser
  // zonder navigatie — zie BRIEFING.md voor dat restrisico.
  const succesBereiktRef = useRef(false);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (hashParams.get('error')) {
      setStatus('ongeldig');
      return undefined;
    }

    let afgehandeld = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        afgehandeld = true;
        setStatus('klaar');
      }
    });

    // Vangnet: als het hash-fragment al vóór het aanmelden van de listener
    // hierboven is verwerkt (race tussen client-init en component-mount) is
    // er inmiddels al een sessie aanwezig.
    if (hashParams.get('type') === 'recovery') {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!afgehandeld && session) {
          afgehandeld = true;
          setStatus('klaar');
        }
      });
    }

    // Ruim verhoogd t.o.v. de oorspronkelijke 3s: op een trage verbinding kon
    // het PASSWORD_RECOVERY-event nog binnenkomen ná de timeout, waardoor
    // "Link verlopen" kort flikkerde voordat status alsnog op 'klaar' sprong.
    const timeout = setTimeout(() => {
      if (!afgehandeld) setStatus('ongeldig');
    }, 8000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
      // Sessie opruimen als de gebruiker wegnavigeert terwijl er wél een
      // recovery-sessie actief was, maar het wachtwoord nooit succesvol is
      // gewijzigd (die geslaagde flow ruimt zichzelf al op, zie handleSubmit).
      if (afgehandeld && !succesBereiktRef.current) {
        supabase.auth.signOut().catch(() => {});
      }
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!nieuwWachtwoord || nieuwWachtwoord.length < 6) {
      setError(t('wachtwoordReset.foutTeKort'));
      return;
    }
    if (nieuwWachtwoord !== herhaalWachtwoord) {
      setError(t('wachtwoordReset.foutMismatch'));
      return;
    }
    setOpslaan(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: nieuwWachtwoord });
      if (err) throw err;
      // Wachtwoord is vanaf hier al gewijzigd — een falende signOut hierna
      // mag de gebruiker niet alsnog een foutmelding tonen (regressie: zie
      // onderdeel 3 van de beveiligingsanalyse).
      succesBereiktRef.current = true;
      setStatus('succes');
      // supabase-js gooit hier normaliter niet, maar geeft { error } terug —
      // zowel dat geval als een onverwachte throw worden hier afgevangen
      // zonder de al-getoonde succesmelding te beïnvloeden.
      try {
        const { error: signOutErr } = await supabase.auth.signOut();
        if (signOutErr) throw signOutErr;
      } catch (signOutErr) {
        console.error('signOut na wachtwoordwijziging mislukt:', signOutErr);
        Sentry.captureException(signOutErr);
      }
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(t('wachtwoordReset.fout') + err.message);
    } finally {
      setOpslaan(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-bg)',
      padding: 20,
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{
            width: 52,
            height: 52,
            background: 'var(--color-primary)',
            borderRadius: 14,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            marginBottom: 16,
          }}>
            💪
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>
            {t('app.name')}
          </h1>
        </div>

        <div className="card">
          {status === 'laden' && (
            <p style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              <span className="spinner" style={{ width: 16, height: 16, marginRight: 8 }} />
              {t('wachtwoordReset.laden')}
            </p>
          )}

          {status === 'klaar' && (
            <form onSubmit={handleSubmit}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                {t('wachtwoordReset.titel')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 20 }}>
                {t('wachtwoordReset.intro')}
              </p>

              {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

              <div className="form-group">
                <label className="form-label" htmlFor="nieuwWachtwoord">{t('wachtwoordReset.labelNieuw')}</label>
                <input
                  id="nieuwWachtwoord"
                  type="password"
                  className="form-input"
                  value={nieuwWachtwoord}
                  onChange={e => setNieuwWachtwoord(e.target.value)}
                  placeholder={t('wachtwoordReset.placeholderNieuw')}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group" style={{ marginBottom: 24 }}>
                <label className="form-label" htmlFor="herhaalWachtwoord">{t('wachtwoordReset.labelHerhaal')}</label>
                <input
                  id="herhaalWachtwoord"
                  type="password"
                  className="form-input"
                  value={herhaalWachtwoord}
                  onChange={e => setHerhaalWachtwoord(e.target.value)}
                  placeholder={t('wachtwoordReset.placeholderNieuw')}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                disabled={opslaan}
              >
                {opslaan ? <><span className="spinner" style={{ width: 16, height: 16 }} /> {t('auth.loading')}</> : t('wachtwoordReset.opslaanKnop')}
              </button>
            </form>
          )}

          {status === 'succes' && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                {t('wachtwoordReset.succesTitel')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 16 }}>
                {t('wachtwoordReset.succesUitleg')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('wachtwoordReset.succesNaarInloggen')}
              </button>
            </div>
          )}

          {status === 'ongeldig' && (
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--color-text)', marginBottom: 6 }}>
                {t('wachtwoordReset.ongeldigTitel')}
              </h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 16 }}>
                {t('wachtwoordReset.ongeldigUitleg')}
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '11px 16px', fontSize: 15 }}
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('wachtwoordReset.naarInloggen')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
